import React, { useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import PerformanceMonitor from "./components/PerformanceMonitor";
import FluidBackground from "./components/FluidBackground";
import CustomCursor from "./components/CustomCursor";
import AIChat from "./components/AIChat";
import SEO from "./components/SEO";

import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { CartProvider } from "./contexts/CartContext";

// Layout Components
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import MobileMenu from "./components/layout/MobileMenu";

// UI Components
import CartDrawer from "./components/ui/CartDrawer";
import CheckoutOverlay from "./components/ui/CheckoutOverlay";

// Lazy-loaded Page Components for code splitting
const HomePage = lazy(() => import("./components/pages/HomePage"));
const GalleryPage = lazy(() => import("./components/pages/GalleryPage"));
const WorksPage = lazy(() => import('./components/pages/WorksPage'));
const ArtistPage = lazy(() => import('./components/pages/ArtistPage'));
const AdminPage = lazy(() => import('./components/pages/AdminPage'));
const SuccessPage = lazy(() => import('./components/pages/SuccessPage'));



// Loading component for Suspense fallback
const PageLoader: React.FC = () => (
  <div className="h-screen bg-black flex items-center justify-center">
    <div className="text-white text-2xl">Loading...</div>
  </div>
);

const AppContent: React.FC = () => {
  const { lang } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navigateToSection = (id: string) => {
    setIsMenuOpen(false);

    // Map section IDs to routes
    const routeMap: Record<string, string> = {
      'hero': '/',
      'gallery': '/gallery',
      'written-works': '/works',
      'artist': '/artist',
      'acquisition': '/artist',
    };

    const targetRoute = routeMap[id] || '/';

    // If we're already on the target route, scroll to section
    if (location.pathname === targetRoute) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // Navigate to the route, then scroll after a delay
      navigate(targetRoute);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  };

  const isAdminPage = location.pathname === '/admin';

  return (
    <div className="relative min-h-screen text-white overflow-x-hidden selection:bg-[#fbbf24] selection:text-black">
      <SEO
        title="CHIMELISART | Galería Virtual"
        description="Explora la obra pictórica y literaria de Francesc Chimelis. Surrealismo figurativo, arte contemporáneo y narrativa inmersiva desde Barcelona."
      />

      {!isAdminPage && <FluidBackground />}
      {!isAdminPage && <CustomCursor text="Ver" />}

      {!isAdminPage && (
        <Header
          onOpenMenu={() => setIsMenuOpen(true)}
          onScrollToSection={navigateToSection}
        />
      )}

      {!isAdminPage && (
        <AnimatePresence>
          <MobileMenu
            isOpen={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
            onScrollToSection={navigateToSection}
          />
        </AnimatePresence>
      )}

      {!isAdminPage && <CartDrawer />}
      {!isAdminPage && <CheckoutOverlay />}

      <main>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage scrollToSection={navigateToSection} />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/works" element={<WorksPage />} />
            <Route path="/artist" element={<ArtistPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/success" element={<SuccessPage />} />
          </Routes>
        </Suspense>
      </main>

      {!isAdminPage && <Footer />}

      {!isAdminPage && <AIChat lang={lang} initialMessage="" />}
      <PerformanceMonitor />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
};

export default App;
